import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { ChevronLeft, ChevronRight } from 'assets/icons';
import ApiDatabase from 'server';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';

const MONTH_LABELS = [
  'Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin',
  'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.',
];

const FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: 'payslips', label: 'Fiches de paie' },
  { value: 'invoices', label: 'Factures client' },
];

const COLORS = {
  payslipImported: '#F59E0B',
  payslipNotImported: '#FCD34D',
  invoicePaid: '#3B82F6',
  invoiceUnpaid: '#EF4444',
  invoiceNotImported: '#93C5FD',
  coeff: '#10B981',
};

const StatsContent = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('xsrfToken');
  const [selectedYear, setSelectedYear] = useState(new Date());
  const [filter, setFilter] = useState('all');
  const [payslipStats, setPayslipStats] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [isWaitingPayslip, setIsWaitingPayslip] = useState(true);
  const [isWaitingInvoice, setIsWaitingInvoice] = useState(true);

  const year = String(selectedYear.getFullYear());
  const isWaiting =
    (filter === 'all' && (isWaitingPayslip || isWaitingInvoice)) ||
    (filter === 'payslips' && isWaitingPayslip) ||
    (filter === 'invoices' && isWaitingInvoice);

  useEffect(() => {
    if (filter === 'all' || filter === 'payslips') {
      setIsWaitingPayslip(true);
      ApiDatabase.payslipStats(
        { token, year },
        (data) => { setPayslipStats(data); setIsWaitingPayslip(false); },
        (err) => { if (err.name !== 'CanceledError') console.log(err); setIsWaitingPayslip(false); }
      );
    }
    if (filter === 'all' || filter === 'invoices') {
      setIsWaitingInvoice(true);
      ApiDatabase.invoiceStats(
        { token, year },
        (data) => { setInvoiceStats(data); setIsWaitingInvoice(false); },
        (err) => { if (err.name !== 'CanceledError') console.log(err); setIsWaitingInvoice(false); }
      );
    }
  }, [year, filter]);

  const goToMonth = (monthStr, type) => {
    if (type === 'invoices') {
      navigate(`/billing/invoices?y=${year}&m=${monthStr}`);
    } else {
      navigate(`/payroll/payslips?y=${year}&m=${monthStr}`);
    }
  };

  const chartData = MONTH_LABELS.map((label, i) => {
    const pMonth = payslipStats?.months?.[i];
    const iMonth = invoiceStats?.months?.[i];

    const pImported = pMonth?.importedAmount || 0;
    const pNotImported = pMonth?.notImportedAmount || 0;
    const pTotal = pMonth?.totalAmount || 0;
    const iPaid = iMonth?.paidAmount || 0;
    const iUnpaid = iMonth?.unpaidAmount || 0;
    const iNotImported = iMonth?.notImportedAmount || 0;
    const iTotal = iMonth?.totalAmount || 0;

    return {
      name: label,
      month: pMonth?.month || iMonth?.month || String(i + 1).padStart(2, '0'),
      payslipImportedAmount: pImported,
      payslipNotImportedAmount: pNotImported,
      payslipAmount: pTotal,
      payslipCount: pMonth?.count || 0,
      payslipImportedCount: pMonth?.importedCount || 0,
      payslipNotImportedCount: pMonth?.notImportedCount || 0,
      invoicePaidAmount: iPaid,
      invoiceUnpaidAmount: iUnpaid,
      invoiceNotImportedAmount: iNotImported,
      invoiceAmount: iTotal,
      invoiceCount: iMonth?.count || 0,
      invoicePaidCount: iMonth?.paidCount || 0,
      invoiceUnpaidCount: iMonth?.unpaidCount || 0,
      invoiceNotImportedCount: iMonth?.notImportedCount || 0,
      coeff: pTotal > 0 ? Math.round((iTotal / pTotal) * 100) / 100 : null,
    };
  });

  const payslipYearTotal = payslipStats?.yearTotal || 0;
  const payslipYearCount = payslipStats?.yearCount || 0;
  const invoiceYearTotal = invoiceStats?.yearTotal || 0;
  const invoiceYearCount = invoiceStats?.yearCount || 0;
  const activeMonths = chartData.filter((m) => m.payslipAmount > 0 && m.invoiceAmount > 0);
  const avgCoeff = activeMonths.length > 0
    ? Math.round((activeMonths.reduce((s, m) => s + m.coeff, 0) / activeMonths.length) * 100) / 100
    : null;

  const fmt = (v) => v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtCoeff = (v) => v != null ? v.toFixed(2) : '-';

  const tooltipFormatter = (value, name) => {
    if (name === 'coeff') return [value != null ? value.toFixed(2) : '-', 'Coefficient'];
    const labels = {
      payslipImportedAmount: 'Paie importées',
      payslipNotImportedAmount: 'Paie non importées',
      invoicePaidAmount: 'Factures payées',
      invoiceUnpaidAmount: 'Factures non payées',
      invoiceNotImportedAmount: 'Factures non importées',
    };
    return [`${fmt(Number(value))} €`, labels[name] || name];
  };

  const legendFormatter = (value) => {
    const labels = {
      payslipImportedAmount: 'Paie importées',
      payslipNotImportedAmount: 'Paie non importées',
      invoicePaidAmount: 'Factures payées',
      invoiceUnpaidAmount: 'Factures non payées',
      invoiceNotImportedAmount: 'Factures non importées',
      coeff: 'Coefficient',
    };
    return labels[value] || value;
  };

  const payslipImportedTotal = chartData.reduce((s, m) => s + m.payslipImportedAmount, 0);
  const payslipNotImportedTotal = chartData.reduce((s, m) => s + m.payslipNotImportedAmount, 0);
  const invoicePaidTotal = chartData.reduce((s, m) => s + m.invoicePaidAmount, 0);
  const invoiceUnpaidTotal = chartData.reduce((s, m) => s + m.invoiceUnpaidAmount, 0);
  const invoiceNotImportedTotal = chartData.reduce((s, m) => s + m.invoiceNotImportedAmount, 0);

  return (
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-sm">
            <button
              className="flex items-center justify-center w-9 h-9 rounded-lg border hover:bg-gray-100"
              onClick={() => setSelectedYear((prev) => new Date(prev.getFullYear() - 1, 0, 1))}
            >
              <ChevronLeft wh={16} color="#374151" />
            </button>
            <DatePicker
              selected={selectedYear}
              onChange={(date) => setSelectedYear(date)}
              showYearPicker
              dateFormat="yyyy"
              className="w-full min-w-[80px] h-10 ft-b border rounded-lg text-center text-sm max-h-[38px]"
              locale="fr-FR"
            />
            <button
              className="flex items-center justify-center w-9 h-9 rounded-lg border hover:bg-gray-100"
              onClick={() => setSelectedYear((prev) => new Date(prev.getFullYear() + 1, 0, 1))}
            >
              <ChevronRight wh={16} color="#374151" />
            </button>
          </div>

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-10 border rounded-lg px-3 text-sm bg-white cursor-pointer"
          >
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        {!isWaiting && (
          <div className="flex gap-3 flex-wrap">
            {(filter === 'all' || filter === 'payslips') && (
              <div className="border rounded py-2 px-4 text-sm font-medium bg-gray-50 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: COLORS.payslipImported }} />
                Paie : {fmt(payslipYearTotal)} € ({payslipYearCount})
              </div>
            )}
            {(filter === 'all' || filter === 'invoices') && (
              <div className="border rounded py-2 px-4 text-sm font-medium bg-gray-50 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: COLORS.invoicePaid }} />
                Factures : {fmt(invoiceYearTotal)} € ({invoiceYearCount})
              </div>
            )}
            {filter === 'all' && avgCoeff != null && (
              <div className="border rounded py-2 px-4 text-sm font-medium bg-gray-50 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: COLORS.coeff }} />
                Coeff. moyen : {fmtCoeff(avgCoeff)}
              </div>
            )}
          </div>
        )}
      </div>

      {isWaiting ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="h-64 w-full bg-gray-100 rounded animate-pulse" />
        </div>
      ) : (
        <div className="flex-1 min-h-0" style={{ minHeight: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 20, right: filter === 'all' ? 50 : 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="amount"
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => `${v.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`}
              />
              {filter === 'all' && (
                <YAxis
                  yAxisId="coeff"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  domain={[0, 'auto']}
                  tickFormatter={(v) => v.toFixed(1)}
                  label={{ value: 'Coeff.', angle: -90, position: 'insideRight', style: { fontSize: 11, fill: '#6B7280' } }}
                />
              )}
              <Tooltip formatter={tooltipFormatter} labelFormatter={(label) => label} />
              <Legend formatter={legendFormatter} />

              {(filter === 'all' || filter === 'payslips') && (
                <Bar
                  yAxisId="amount"
                  dataKey="payslipNotImportedAmount"
                  name="payslipNotImportedAmount"
                  stackId="payslips"
                  fill={COLORS.payslipNotImported}
                  maxBarSize={filter === 'all' ? 32 : 48}
                  cursor="pointer"
                  onClick={(data) => { if (data?.month) goToMonth(data.month, 'payslips'); }}
                />
              )}
              {(filter === 'all' || filter === 'payslips') && (
                <Bar
                  yAxisId="amount"
                  dataKey="payslipImportedAmount"
                  name="payslipImportedAmount"
                  stackId="payslips"
                  fill={COLORS.payslipImported}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={filter === 'all' ? 32 : 48}
                  cursor="pointer"
                  onClick={(data) => { if (data?.month) goToMonth(data.month, 'payslips'); }}
                />
              )}

              {(filter === 'all' || filter === 'invoices') && (
                <Bar
                  yAxisId="amount"
                  dataKey="invoiceNotImportedAmount"
                  name="invoiceNotImportedAmount"
                  stackId="invoices"
                  fill={COLORS.invoiceNotImported}
                  maxBarSize={filter === 'all' ? 32 : 48}
                  cursor="pointer"
                  onClick={(data) => { if (data?.month) goToMonth(data.month, 'invoices'); }}
                />
              )}
              {(filter === 'all' || filter === 'invoices') && (
                <Bar
                  yAxisId="amount"
                  dataKey="invoiceUnpaidAmount"
                  name="invoiceUnpaidAmount"
                  stackId="invoices"
                  fill={COLORS.invoiceUnpaid}
                  maxBarSize={filter === 'all' ? 32 : 48}
                  cursor="pointer"
                  onClick={(data) => { if (data?.month) goToMonth(data.month, 'invoices'); }}
                />
              )}
              {(filter === 'all' || filter === 'invoices') && (
                <Bar
                  yAxisId="amount"
                  dataKey="invoicePaidAmount"
                  name="invoicePaidAmount"
                  stackId="invoices"
                  fill={COLORS.invoicePaid}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={filter === 'all' ? 32 : 48}
                  cursor="pointer"
                  onClick={(data) => { if (data?.month) goToMonth(data.month, 'invoices'); }}
                />
              )}

              {filter === 'all' && (
                <Line
                  yAxisId="coeff"
                  type="monotone"
                  dataKey="coeff"
                  name="coeff"
                  stroke={COLORS.coeff}
                  strokeWidth={2}
                  dot={{ r: 4, fill: COLORS.coeff }}
                  connectNulls={false}
                >
                  <LabelList
                    dataKey="coeff"
                    position="top"
                    formatter={(v) => v != null ? v.toFixed(2) : ''}
                    style={{ fontSize: 11, fill: '#059669', fontWeight: 600 }}
                  />
                </Line>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {!isWaiting && (
        <div className="overflow-auto flex-shrink-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-3 py-2">Mois</th>
                {(filter === 'all' || filter === 'payslips') && (
                  <>
                    <th className="px-3 py-2 text-right">Fiches</th>
                    <th className="px-3 py-2 text-right">Importées</th>
                    <th className="px-3 py-2 text-right">Non importées</th>
                    <th className="px-3 py-2 text-right">Total Paie</th>
                  </>
                )}
                {(filter === 'all' || filter === 'invoices') && (
                  <>
                    <th className="px-3 py-2 text-right">Factures</th>
                    <th className="px-3 py-2 text-right">Payées</th>
                    <th className="px-3 py-2 text-right">Non payées</th>
                    <th className="px-3 py-2 text-right">Non importées</th>
                    <th className="px-3 py-2 text-right">Total Factures</th>
                  </>
                )}
                {filter === 'all' && (
                  <th className="px-3 py-2 text-right">Coeff.</th>
                )}
              </tr>
            </thead>
            <tbody>
              {chartData.map((m, i) => (
                <tr key={m.month} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2">{MONTH_LABELS[i]}</td>
                  {(filter === 'all' || filter === 'payslips') && (
                    <>
                      <td
                        className="px-3 py-2 text-right cursor-pointer hover:text-amber-600"
                        onClick={() => goToMonth(m.month, 'payslips')}
                      >
                        {m.payslipCount}
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: COLORS.payslipImported }}>
                        {fmt(m.payslipImportedAmount)} €
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: COLORS.payslipNotImported }}>
                        {fmt(m.payslipNotImportedAmount)} €
                      </td>
                      <td
                        className="px-3 py-2 text-right cursor-pointer hover:text-amber-600"
                        onClick={() => goToMonth(m.month, 'payslips')}
                      >
                        {fmt(m.payslipAmount)} €
                      </td>
                    </>
                  )}
                  {(filter === 'all' || filter === 'invoices') && (
                    <>
                      <td
                        className="px-3 py-2 text-right cursor-pointer hover:text-blue-600"
                        onClick={() => goToMonth(m.month, 'invoices')}
                      >
                        {m.invoiceCount}
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: COLORS.invoicePaid }}>
                        {fmt(m.invoicePaidAmount)} €
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: COLORS.invoiceUnpaid }}>
                        {fmt(m.invoiceUnpaidAmount)} €
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: COLORS.invoiceNotImported }}>
                        {fmt(m.invoiceNotImportedAmount)} €
                      </td>
                      <td
                        className="px-3 py-2 text-right cursor-pointer hover:text-blue-600"
                        onClick={() => goToMonth(m.month, 'invoices')}
                      >
                        {fmt(m.invoiceAmount)} €
                      </td>
                    </>
                  )}
                  {filter === 'all' && (
                    <td className="px-3 py-2 text-right font-medium" style={{ color: COLORS.coeff }}>
                      {fmtCoeff(m.coeff)}
                    </td>
                  )}
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 font-semibold">
                <td className="px-3 py-2">Total</td>
                {(filter === 'all' || filter === 'payslips') && (
                  <>
                    <td className="px-3 py-2 text-right">{payslipYearCount}</td>
                    <td className="px-3 py-2 text-right" style={{ color: COLORS.payslipImported }}>
                      {fmt(payslipImportedTotal)} €
                    </td>
                    <td className="px-3 py-2 text-right" style={{ color: COLORS.payslipNotImported }}>
                      {fmt(payslipNotImportedTotal)} €
                    </td>
                    <td className="px-3 py-2 text-right">{fmt(payslipYearTotal)} €</td>
                  </>
                )}
                {(filter === 'all' || filter === 'invoices') && (
                  <>
                    <td className="px-3 py-2 text-right">{invoiceYearCount}</td>
                    <td className="px-3 py-2 text-right" style={{ color: COLORS.invoicePaid }}>
                      {fmt(invoicePaidTotal)} €
                    </td>
                    <td className="px-3 py-2 text-right" style={{ color: COLORS.invoiceUnpaid }}>
                      {fmt(invoiceUnpaidTotal)} €
                    </td>
                    <td className="px-3 py-2 text-right" style={{ color: COLORS.invoiceNotImported }}>
                      {fmt(invoiceNotImportedTotal)} €
                    </td>
                    <td className="px-3 py-2 text-right">{fmt(invoiceYearTotal)} €</td>
                  </>
                )}
                {filter === 'all' && (
                  <td className="px-3 py-2 text-right font-medium" style={{ color: COLORS.coeff }}>
                    {fmtCoeff(avgCoeff)}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StatsContent;
